import ko from 'knockout';
import _ from 'underscore';
import { formatCost, formatDecimal, NOT_AVAILABLE_DEFAULT_STR } from '@bingads-webui-campaign/recommendation-core';
import { SELECT_TYPE, COMMENT_TYPES } from '../const';

function interpolation(y1, y2, x1, x2, x) {
  if (x1 !== x2) {
    return (((y1 - y2) / (x1 - x2)) * (x - x2)) + y2;
  }
  return y2;
}

export default class ViewModel {
  constructor({
    i18n,
    view,
    data,
    fromCampaign,
    toCampaign,
    type,
    deps,
    readOnly,
  }) {
    this.view = view;
    this.i18n = i18n;
    this.data = data;
    this.deps = deps;
    this.readOnly = readOnly;
    this.opportunityType = type;

    const suggestBudget = Math.round(toCampaign.BudgetDeficit * 100) / 100;
    const surplusBudget = Math.round(fromCampaign.BudgetSurplus * 100) / 100;
    const minBudget = deps.appConfig.get('BudgetRange').DailyRange.FloorValue;
    const maxBudget = deps.appConfig.get('BudgetRange').DailyRange.CeilingValue;
    const maxInputBudget = Math.round(Math.min(
      maxBudget - toCampaign.CurrentBudget,
      fromCampaign.CurrentBudget - minBudget
    ) * 100) / 100;
    const fromCampaignCurrent = i18n.getString(
      'Recommendation_ReallocateBudgetInline_CurrentBudget_Title',
      { budget: formatCost(fromCampaign.CurrentBudget, i18n, i18n.currency) }
    );
    const toCampaignCurrent = i18n.getString(
      'Recommendation_ReallocateBudgetInline_CurrentBudget_Title',
      { budget: formatCost(toCampaign.CurrentBudget, i18n, i18n.currency) }
    );
    const fromCampaignName = fromCampaign.CampaignName;
    const toCampaignName = toCampaign.CampaignName;
    this.budgetType = ko.observable(SELECT_TYPE.SuggestBudget);
    this.enableInput = ko.computed(() => this.budgetType() === SELECT_TYPE.ManualBudget);
    this.currencySymbol = i18n.getCurrencySymbol(deps.pageContext.AccountCurrencyCode);

    this.suggestBudget = formatDecimal(suggestBudget, i18n);
    this.suggestBudgetStr = `${formatCost(suggestBudget, i18n, i18n.currency)} ${i18n.getString('Recommendation_ReallocateBudgetInline_BudgetType_SuggestBudget_Title')}`;

    this.inputBudget = ko.observable('');
    this.applyBudget = ko.computed(() =>
      (this.budgetType() === SELECT_TYPE.SuggestBudget ?
        this.suggestBudget : this.inputBudget()));

    this.errorMessage = ko.observable('');
    this.warnMessage = ko.observable('');
    this.hadInput = ko.observable(false);
    this.enableApply = ko.computed(() => _.isEmpty(this.errorMessage()));

    this.deltaInfo = ko.observable({});

    this.setDeltaInfo = (value, validBudget = true) => {
      let deltaFromBudget = NOT_AVAILABLE_DEFAULT_STR;
      let deltaToBudget = NOT_AVAILABLE_DEFAULT_STR;
      let fromCampaignClicks = NOT_AVAILABLE_DEFAULT_STR;
      let fromCampaignCost = NOT_AVAILABLE_DEFAULT_STR;
      let toCampaignClicks = NOT_AVAILABLE_DEFAULT_STR;
      let toCampaignCost = NOT_AVAILABLE_DEFAULT_STR;
      const fromCampaignValid = !validBudget || value <= surplusBudget;
      const newLandscape = _.filter(toCampaign.Landscapes, ({
        Comment,
      }) => Comment !== COMMENT_TYPES.MAXVALUE);
      const sorted = _.sortBy(newLandscape, 'EstimatedBudget');

      if (validBudget && !_.isEmpty(sorted)) {
        deltaFromBudget = `-${formatCost(value, i18n, i18n.currency)}`;
        deltaToBudget = `+${formatCost(value, i18n, i18n.currency)}`;
        fromCampaignClicks = `+${formatDecimal(0, i18n)}`;
        fromCampaignCost = `+${formatCost(0, i18n, i18n.currency)}`;

        const estBudget = value + toCampaign.CurrentBudget;
        let minPoint = {
          EstimatedBudget: 0,
          EstimatedClicks: 0,
          EstimatedCost: 0,
        };
        let maxPoint = minPoint;

        const nextIndex = _.findIndex(sorted, point => estBudget <= point.EstimatedBudget);
        if (nextIndex < 0) {
          minPoint = _.last(sorted);
          maxPoint = _.last(sorted);
        } else if (nextIndex === 0) {
          maxPoint = _.first(sorted);
        } else {
          minPoint = sorted[nextIndex - 1];
          maxPoint = sorted[nextIndex];
        }

        toCampaignClicks = `+${formatDecimal(Math.round(interpolation(
          minPoint.EstimatedClicks,
          maxPoint.EstimatedClicks,
          minPoint.EstimatedBudget,
          maxPoint.EstimatedBudget,
          estBudget
        ) - toCampaign.CurrentClicks), i18n)}`;
        toCampaignCost = `+${formatCost(interpolation(
          minPoint.EstimatedCost,
          maxPoint.EstimatedCost,
          minPoint.EstimatedBudget,
          maxPoint.EstimatedBudget,
          estBudget
        ) - toCampaign.CurrentCost, i18n, i18n.currency)}`;
      }
      this.deltaInfo({
        fromCampaignValid,
        deltaFromBudget,
        deltaToBudget,
        fromCampaignName,
        toCampaignName,
        fromCampaignCurrent,
        fromCampaignClicks,
        fromCampaignCost,
        toCampaignCurrent,
        toCampaignClicks,
        toCampaignCost,
      });
    };

    this.setDeltaInfo(suggestBudget);

    this.applyBudget.subscribe((inputValue) => {
      let errorMessage = '';
      let warnMessage = '';
      let value = 0;

      if (this.enableInput() && !_.isEmpty(inputValue)) {
        this.hadInput(true);
      }

      if (!_.isEmpty(inputValue) && i18n.isValidDecimal(inputValue, true)) {
        value = i18n.parseDecimal(inputValue);
        const [, decimalStr] = value.toString().split('.');

        if (decimalStr && decimalStr.length > 2) {
          errorMessage = i18n.getString('Validation_Number_Max_Length');
        } else if (value > maxInputBudget || value < 0) {
          errorMessage = i18n.getString('BudgetSuggestionsValidationMsg')
            .replace('{0}', 0)
            .replace('{1}', i18n.currency)
            .replace('{2}', maxInputBudget)
            .replace('{3}', i18n.currency);
        } else if (value < suggestBudget) {
          warnMessage = i18n.getString(
            'Recommendation_ReallocateBudgetInline_ErrorMessage_WarningMsg',
            {
              name: toCampaign.CampaignName,
            }
          );
        } else if (value > surplusBudget) {
          warnMessage = i18n.getString(
            'Recommendation_ReallocateBudgetInline_ErrorMessage_WarningMsg',
            {
              name: fromCampaign.CampaignName,
            }
          );
        }
      } else if (this.hadInput()) {
        errorMessage = i18n.getString('ErrorMessage_InputNumericValue');
      }

      this.setDeltaInfo(value, !_.isEmpty(inputValue) && _.isEmpty(errorMessage));

      this.errorMessage(errorMessage);
      this.warnMessage(warnMessage);
    });

    return this;
  }
}
